/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useTheme } from '@coursera/cds-core';

import ClipsTranscriptToolbar from 'bundles/clips/components/ClipsTranscriptToolbar';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { VideoContext } from 'bundles/clips/components/ClipsVideoRootContainer';
import { InteractiveTranscript } from 'bundles/interactive-transcript';
import type Track from 'bundles/interactive-transcript/models/Track';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';
import userPreferences from 'bundles/video-player/utils/userPreferences';

type Props = {
  tracks: TrackList;
  courseId?: string;
  itemId?: string;
};

const ClipsTranscriptContainer = ({ tracks, itemId, courseId }: Props) => {
  const theme = useTheme();
  const { videoPlayer, isPlaying } = React.useContext(VideoContext);
  const [time, setTime] = React.useState(0);
  const [isTimeUpdateHandlerSet, setIsTimeUpdateHandlerSet] = React.useState(false);

  const handleTimeChange = (t: number) => {
    videoPlayer?.seekTo(t);
    if (!isPlaying) {
      videoPlayer?.play();
    }
  };

  const handlePausePlayer = () => {
    videoPlayer?.pause();
  };

  const handleTimeUpdate = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (t: number) => {
      setTime(t);
    },
    [setTime]
  );

  // Set the time update event handler once
  React.useEffect(() => {
    if (videoPlayer && !isTimeUpdateHandlerSet) {
      videoPlayer.addTimeUpdateEventHandler(handleTimeUpdate);
      setIsTimeUpdateHandlerSet(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlayer, isTimeUpdateHandlerSet]);

  // TODO: the default track should be set using the CoreLex video player
  // before CoreLex video player is integrated, temporarily set it to the first track
  const defaultSubtitleLanguage = userPreferences.get('subtitleLanguage') || 'en';
  let defaultTrack = tracks?.toArray().find((track: Track) => track.languageCode === defaultSubtitleLanguage);

  if (!defaultTrack) {
    defaultTrack = tracks?.toArray().find((track: Track) => track.languageCode === 'en');
  }
  const [selectedTrack, setSelectedTrack] = React.useState<Track>(defaultTrack);

  if (!defaultTrack) {
    return null;
  }

  // NOTE that the <ClipsTranscriptToolbar> is NOT a controlled componnet,
  // and `selectedTrack` is not required for showing the current selected value.
  const handleSelectedTrackChange = (track: Track) => {
    if (track) {
      setSelectedTrack(track);
      userPreferences.set('subtitleLanguage', track.languageCode);
    }
  };

  return (
    <div css={{ marginTop: 'var(--cds-spacing-200)' }}>
      {tracks && (
        <div css={{ marginBottom: 'var(--cds-spacing-200)', marginLeft: '-8px' }}>
          <ClipsTranscriptToolbar
            tracks={tracks}
            selectedTrack={selectedTrack}
            onTrackSelected={handleSelectedTrackChange}
            courseId={courseId}
            itemId={itemId}
          />
        </div>
      )}
      {selectedTrack && (
        <InteractiveTranscript
          time={time}
          timeDiff={undefined}
          track={selectedTrack}
          highlights={[]}
          onTimeChange={handleTimeChange}
          handlePausePlayer={handlePausePlayer}
          disableHighlighting
        />
      )}
    </div>
  );
};

export default ClipsTranscriptContainer;
